/**
 * The contacts manager's inline "add a contact" form.
 *
 * Renders a block-level `<form>` (not a `<tr>`): the manager places it above
 * the table on md+ and above the cards below md, so one component serves both.
 *
 * The POST body mirrors `ContactFormModal`'s `buildCreate`:
 *   - `name`, `firstName` and `lastName` are ALWAYS sent (`lastName` null when
 *     blank), so every contact starts with its parts stored (owner, 2026-10-16);
 *   - `companyId` is the manager's company — a contact raised here must belong
 *     to it, because the server checks a contact's company for existence only;
 *   - every other blank field is OMITTED rather than sent as '' — the row starts
 *     with real nulls and the server's own defaults apply (layout "Standard",
 *     emailOptOut false). `layout`, owner and staff ids are never sent.
 *   - `contactGroupId` only when a group is picked; an inactive id is a 422, so
 *     only active groups are offered.
 *
 * Validation and error routing are the modal's: first name and full name
 * required, the same loose email shape and the server's max lengths; a server
 * field error on a field shown here lands under it, anything else (a `.strict()`
 * key, `companyId`, a 403) goes to the danger Notice.
 *
 * The three name fields fill each other both ways through `applyNameEdit`.
 * Enter or Ctrl/Cmd+Enter adds; Escape cancels (stopped here so it does not
 * also close the manager's Modal).
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { CONTACT_NAME_LABELS, applyNameEdit } from '@/lib/contact-name';
import { Button, Combobox, Field, Input } from '@/components/ui';
import { ContactTitleInput } from '@/components/contacts/ContactTitleInput';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type { Contact, ContactGroup, ContactInput } from '@/lib/types';

export interface ContactAddRowProps {
  companyId: number;
  groups: ContactGroup[];
  onCreated: (contact: Contact) => void;
  onCancel: () => void;
}

type AddField = 'firstName' | 'lastName' | 'name' | 'title' | 'email' | 'phone' | 'contactGroupId';

type AddForm = Record<AddField, string>;

const BLANK: AddForm = {
  firstName: '',
  lastName: '',
  name: '',
  title: '',
  email: '',
  phone: '',
  contactGroupId: '',
};

/** Mirrors the server's max lengths (same table as ContactFormModal). */
const LIMITS: Partial<Record<AddField, number>> = {
  name: 240,
  firstName: 120,
  lastName: 120,
  title: 120,
  email: 255,
  phone: 32,
};

const LABELS: Record<AddField, string> = {
  firstName: CONTACT_NAME_LABELS.firstName,
  lastName: CONTACT_NAME_LABELS.lastName,
  name: CONTACT_NAME_LABELS.name,
  title: 'Title',
  email: 'Email',
  phone: 'Phone',
  contactGroupId: 'Group',
};

const KNOWN_FIELDS = new Set<string>(Object.keys(LABELS));

/* Deliberately loose, as in ContactFormModal: the server checks the format. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: AddForm): Partial<Record<AddField, string>> {
  const errors: Partial<Record<AddField, string>> = {};
  if (!form.firstName.trim()) errors.firstName = 'Enter the contact’s first name.';
  if (!form.name.trim()) errors.name = 'Enter the contact’s full name.';
  for (const [key, max] of Object.entries(LIMITS) as [AddField, number][]) {
    if (errors[key]) continue;
    if (form[key].trim().length > max) {
      errors[key] = `${LABELS[key]} must be ${max} characters or fewer.`;
    }
  }
  const email = form.email.trim();
  if (email && !EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address.';
  return errors;
}

function buildCreate(form: AddForm, companyId: number): ContactInput {
  const body: ContactInput = {
    name: form.name.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim() || null,
    companyId,
  };
  for (const key of ['title', 'email', 'phone'] as const) {
    const value = form[key].trim();
    if (value) body[key] = value;
  }
  if (form.contactGroupId) body.contactGroupId = Number(form.contactGroupId);
  return body;
}

export function ContactAddRow({ companyId, groups, onCreated, onCancel }: ContactAddRowProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<AddForm>(BLANK);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const groupOptions: ComboboxOption[] = groups
    .filter((group) => group.isActive)
    .map((group) => ({
      value: String(group.id),
      label: group.name.trim() === '' ? `#${group.id}` : group.name.trim(),
    }));

  function clearErrors(keys: readonly string[]) {
    setErrors((previous) => {
      if (!keys.some((key) => key in previous)) return previous;
      const next = { ...previous };
      for (const key of keys) delete next[key];
      return next;
    });
  }

  function setField(key: AddField, value: string) {
    if (key !== 'firstName' && key !== 'lastName' && key !== 'name') {
      setForm((previous) => ({ ...previous, [key]: value }));
      clearErrors([key]);
      return;
    }
    setForm((previous) => ({
      ...previous,
      ...applyNameEdit(
        { firstName: previous.firstName, lastName: previous.lastName, name: previous.name },
        key,
        value,
      ),
    }));
    const current = { firstName: form.firstName, lastName: form.lastName, name: form.name };
    const edited = applyNameEdit(current, key, value);
    clearErrors(
      (['firstName', 'lastName', 'name'] as const).filter(
        (field) => field === key || edited[field] !== current[field],
      ),
    );
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // The form sits inside the manager's Modal; keep the submit from reaching
    // any form above it in the React tree.
    event.stopPropagation();
    if (saving) return;

    const clientErrors = validate(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);
    try {
      const result = await api.createContact(buildCreate(form, companyId));
      onCreated(result.data);
      setForm(BLANK);
      firstNameRef.current?.focus();
    } catch (error) {
      if (error instanceof ApiError) {
        const mapped: Record<string, string> = {};
        const unmapped: string[] = [...error.formMessages];
        for (const [path, message] of Object.entries(error.fieldErrors)) {
          if (KNOWN_FIELDS.has(path)) mapped[path] = message;
          else unmapped.push(`${path}: ${message}`);
        }
        setErrors(mapped);
        if (unmapped.length > 0) setFormError(`${error.message} — ${unmapped.join('; ')}`);
        else if (Object.keys(mapped).length > 0) setFormError(null);
        else setFormError(error.message);
      } else {
        setFormError('Couldn’t reach the server. Check your connection.');
      }
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Escape') {
      if (event.defaultPrevented) return;
      event.stopPropagation();
      event.preventDefault();
      if (!saving) onCancel();
      return;
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      formRef.current?.requestSubmit();
    }
  }

  function textField(
    key: Exclude<AddField, 'contactGroupId'>,
    options: { type?: string; required?: boolean; autoComplete?: string } = {},
  ) {
    return (
      <Field label={LABELS[key]} required={options.required} error={errors[key]}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Input
            id={id}
            ref={key === 'firstName' ? firstNameRef : undefined}
            aria-describedby={describedBy}
            name={key}
            type={options.type ?? 'text'}
            value={form[key]}
            maxLength={LIMITS[key]}
            autoComplete={options.autoComplete ?? 'off'}
            invalid={Boolean(errors[key])}
            disabled={saving}
            onChange={(event) => setField(key, event.target.value)}
          />
        )}
      </Field>
    );
  }

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      aria-label="Add contact"
      className="grid gap-3 rounded-lg border border-border bg-surface p-3"
    >
      {formError && (
        <Notice tone="danger" role="alert">
          {formError}
        </Notice>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {textField('firstName', { required: true, autoComplete: 'given-name' })}
        {textField('lastName', { autoComplete: 'family-name' })}
        {textField('name', { required: true, autoComplete: 'name' })}
        <Field label={LABELS.title} error={errors.title}>
          {({ id, 'aria-describedby': describedBy }) => (
            <ContactTitleInput
              id={id}
              aria-describedby={describedBy}
              value={form.title}
              onChange={(value) => setField('title', value)}
              disabled={saving}
              invalid={Boolean(errors.title)}
            />
          )}
        </Field>
        {textField('email', { type: 'email', autoComplete: 'email' })}
        {textField('phone', { type: 'tel' })}
        <Field label={LABELS.contactGroupId} error={errors.contactGroupId}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              aria-describedby={describedBy}
              value={form.contactGroupId}
              onChange={(value) => setField('contactGroupId', value)}
              options={groupOptions}
              placeholder="No group"
              emptyOptionLabel="No group"
              searchPlaceholder="Search groups…"
              disabled={saving}
              invalid={Boolean(errors.contactGroupId)}
            />
          )}
        </Field>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" loading={saving}>
          Add
        </Button>
      </div>
    </form>
  );
}
